import React, { useCallback, useEffect, useState } from "react";
import API, { ensureCsrfToken } from "../services/api";
import UserAccessForm from "../components/access/UserAccessForm";
import ResourceGrantForm from "../components/access/ResourceGrantForm";
import ResourceOwnershipForm from "../components/access/ResourceOwnershipForm";
import { accessError } from "../utils/accessManagement";
import "./AccessManagementPage.css";

export default function AccessManagementPage({ access, theme }) {
    const [catalog, setCatalog] = useState(null);
    const [selectedId, setSelectedId] = useState("");
    const [creating, setCreating] = useState(false);
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState("");
    const [message, setMessage] = useState("");
    const [revision, setRevision] = useState(0);
    const permitted = Boolean(access?.can_manage_users);
    const superadmin = access?.role === "superadmin";
    const load = useCallback(async (signal) => {
        const response = await API.get("/access/catalog/", { signal });
        setCatalog(response.data);
    }, []);
    useEffect(() => {
        if (!permitted) return undefined;
        const controller = new AbortController();
        load(controller.signal).catch((failure) => {
            if (!controller.signal.aborted) setError(accessError(failure));
        });
        return () => controller.abort();
    }, [permitted, load]);
    const mutate = async (operation, success) => {
        setBusy(true);
        setMessage("");
        setError("");
        let saved = false;
        try {
            const token = await ensureCsrfToken();
            if (!token) throw new Error("Reload this page to refresh your session before saving.");
            const response = await operation();
            saved = true;
            setMessage(success);
            try { await load(); } catch {
                setError("The change was saved, but the resource list could not be refreshed. Refresh before making further changes.");
            }
            return response;
        } catch (failure) {
            throw new Error(saved ? "The change was saved. Refresh this page to see the latest state." : failure.response ? accessError(failure) : failure.message || "Unable to save changes.");
        } finally { setBusy(false); }
    };
    if (!permitted) return <main className="access-page" data-theme={theme}><h1>User access</h1><p role="alert">Access management is not available for this session.</p></main>;
    if (!catalog) return <main className="access-page" data-theme={theme}><h1>User access</h1>{error ? <><p role="alert">{error}</p><button onClick={() => { setError(""); load().catch((failure) => setError(accessError(failure))); }}>Retry</button></> : <p role="status">Loading users and resources…</p>}</main>;
    const selected = catalog.users.find((row) => row.id === Number(selectedId));
    const administrators = catalog.users.filter((row) => row.role === "admin" && !row.administrator);
    return <main className="access-page" data-theme={theme}>
        <header><p className="access-eyebrow">Administration</p><h1>User access</h1><p>Manage account roles, expiry, capacity and resource access for your {superadmin ? "administrators and users" : "users"}.</p></header>
        {access.enforced === false && <p className="access-hint" role="status">Setup mode: policies are saved, but account restrictions are not yet enforced. Existing users keep their current access.</p>}
        {message && <p className="access-success" role="status">{message}</p>}
        {error && <p className="access-error" role="alert">{error}</p>}
        <section className="access-panel" aria-label="Select user">
            <div className="access-toolbar">
                <label>User<select disabled={busy} value={creating ? "" : selectedId} onChange={(event) => { setSelectedId(event.target.value); setCreating(false); setMessage(""); }}>
                    <option value="">Select user</option>{catalog.users.map((row) => <option key={row.id} value={row.id}>{row.email} — {row.role || "not assigned"}</option>)}
                </select></label>
                <button type="button" disabled={busy} onClick={() => { setCreating(true); setSelectedId(""); setMessage(""); setRevision((value) => value + 1); }}>Create user</button>
            </div>
            {!selected && !creating && <p>Select an account to manage its policy, or create a user.</p>}
        </section>
        {(creating || selected) && <UserAccessForm key={`${creating ? "new" : selected.id}:${revision}`} user={creating ? null : selected} administrators={administrators} superadmin={superadmin} ownUserId={access.user_id} busy={busy} onSave={async (data) => {
            const response = await mutate(() => creating ? API.post("/access/users/", data) : API.patch(`/access/users/${selected.id}/`, data), creating ? "User created." : "Account policy saved.");
            setCreating(false);
            setSelectedId(String(response.data.id));
            setRevision((value) => value + 1);
        }} />}
        {!creating && selected?.role && !["admin", "superadmin"].includes(selected.role) && <ResourceGrantForm key={selected.id} user={selected} catalog={catalog} busy={busy} onSave={(data) => mutate(() => API.put(`/access/users/${selected.id}/grants/`, data), "Resource access saved.")} />}
        {!creating && selected && ["admin", "superadmin"].includes(selected.role) && <p className="access-hint">{selected.role === "superadmin" ? "Superadmins have access to all resources without individual grants." : "Administrators inherit their allocated server pool. Use resource ownership below to allocate existing resources."}</p>}
        {superadmin && <ResourceOwnershipForm catalog={catalog} administrators={administrators} busy={busy} onSave={(kind, id, owner) => mutate(() => API.put(`/access/${kind}/${id}/`, { administrator: owner }), "Resource ownership assigned.")} />}
    </main>;
}
